import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from '../lib/authService';
import toast from 'react-hot-toast';
import PwaInstallBanner from '../components/common/PwaInstallBanner';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const { currentUser, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate('/', { replace: true });
    }
  }, [authLoading, currentUser, navigate]);

  useEffect(() => {
    if (window.authError) {
      setError(window.authError);
      delete window.authError;
    }
    const t = setTimeout(() => {
      const ev = emailRef.current?.value;
      const pv = passwordRef.current?.value;
      if (ev && !email) setEmail(ev);
      if (pv && !password) setPassword(pv);
    }, 150);
    return () => clearTimeout(t);
  }, [email, password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formData = new FormData(e.currentTarget);
      const emailVal = (formData.get('email') || email || emailRef.current?.value || '').toString().trim().toLowerCase();
      const passwordVal = (formData.get('password') || password || passwordRef.current?.value || '').toString();
      if (!emailVal || !passwordVal) {
        throw new Error('Mohon isi email dan password.');
      }
      await signInWithEmailAndPassword(null, emailVal, passwordVal);
      navigate('/');
    } catch (err) {
      let errorMessage = 'Gagal untuk login. Periksa kembali email dan password Anda.';
      if (err.status === 401 || err.message?.includes('tidak valid')) {
        errorMessage = 'Email atau password tidak valid.';
      } else if (err.message?.includes('API tidak merespons JSON')) {
        errorMessage = 'Server API belum terhubung. Hubungi admin untuk mengaktifkan backend login.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#D1D0D0] via-white to-white">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl overflow-hidden shadow-xl ring-2 ring-brand-600/30">
            <img src="/hr-logo.svg" alt="Dewatanet HRIS" className="h-full w-full object-cover" />
          </div>
           <h2 className="mt-6 text-3xl font-bold text-[#000000] dark:text-[#D1D0D0]">Selamat Datang</h2>
           <p className="mt-2 text-sm text-[#988686] dark:text-[#D1D0D0]/80">Masuk ke akun HRIS Anda</p>
        </div>

       <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-[#D1D0D0] dark:border-neutral-700 p-8">
          {error && (
            <div className="mb-6 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} noValidate className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm mb-1 text-[#000000] dark:text-[#D1D0D0]">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                ref={emailRef}
                required
                className="w-full px-3 py-2 rounded-md border border-[#D1D0D0] dark:border-[#988686] bg-white dark:bg-neutral-900 text-[#000000] dark:text-[#D1D0D0] placeholder-[#988686] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm mb-1 text-[#000000] dark:text-[#D1D0D0]">Kata Sandi</label>
              <input
                type="password"
                id="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                ref={passwordRef}
                required
                className="w-full px-3 py-2 rounded-md border border-[#D1D0D0] dark:border-[#988686] bg-white dark:bg-neutral-900 text-[#000000] dark:text-[#D1D0D0] placeholder-[#988686] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
             className="w-full inline-flex justify-center items-center gap-2 py-3 px-4 rounded-lg text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-4 focus:ring-brand-600/30 shadow-[0_8px_24px_rgba(92,78,78,0.35)] disabled:opacity-60 transition"
            >
              {loading ? 'Memproses…' : 'Masuk'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-[#988686]">Belum punya akun? </span>
            <Link
              to="/signup"
              className="font-semibold text-brand-600 hover:text-brand-700 underline-offset-2 hover:underline"
            >
              Daftar
            </Link>
          </div>
        </div>

        <div className="text-center text-xs text-[#988686]">
          © {new Date().getFullYear()} HRIS
        </div>
      </div>
      <PwaInstallBanner />
    </div>
  );
};

export default LoginPage;
